/**
 * Reusable LiteraryStructureBadge component with tooltip support
 * Displays literary structure features with explanations
 * Supports both hover (desktop) and tap (mobile) interactions
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Repeat, Brackets, Scale, ArrowRight } from 'lucide-react';

interface LiteraryStructureBadgeProps {
  structure: 'chiasm' | 'inclusio' | 'parallelism' | 'transition' | string;
  className?: string;
}

// Icon mapping for literary structures
const STRUCTURE_ICONS = {
  chiasm: Repeat,
  inclusio: Brackets,
  parallelism: Scale,
  transition: ArrowRight
};

// Color schemes for each structure type
const STRUCTURE_COLORS = {
  chiasm: 'bg-purple-50 text-purple-700 border-purple-200',
  inclusio: 'bg-green-50 text-green-700 border-green-200',
  parallelism: 'bg-blue-50 text-blue-700 border-blue-200',
  transition: 'bg-amber-50 text-amber-700 border-amber-200'
};

// Labels for display
const STRUCTURE_LABELS: Record<string, string> = {
  chiasm: 'Chiasm',
  inclusio: 'Inclusio',
  parallelism: 'Parallelism',
  transition: 'Transition'
};

// Detailed explanations for tooltips
const STRUCTURE_DESCRIPTIONS: Record<string, { title: string; description: string }> = {
  chiasm: {
    title: 'Chiasm (Chiastic Structure)',
    description: 'A literary pattern where ideas are presented in order (A-B-C) and then repeated in reverse order (C-B-A), creating a mirror-like structure that emphasizes the central point.'
  },
  inclusio: {
    title: 'Inclusio (Bracketing)',
    description: 'A literary device where a passage begins and ends with the same or similar words, phrases, or themes, creating a frame that marks the boundaries of a literary unit.'
  },
  parallelism: {
    title: 'Parallelism',
    description: 'A poetic device where successive lines or phrases have similar grammatical structure, rhythm, or meaning, often used to emphasize, contrast, or develop an idea.'
  },
  transition: {
    title: 'Transition',
    description: 'A literary marker that signals a shift in topic, speaker, time, location, or theme, helping to organize the text into distinct sections.'
  }
};

export function LiteraryStructureBadge({ structure, className = '' }: LiteraryStructureBadgeProps) {
  const [open, setOpen] = useState(false);
  
  const normalizedStructure = structure.toLowerCase();
  const Icon = STRUCTURE_ICONS[normalizedStructure as keyof typeof STRUCTURE_ICONS];
  const colorClass = STRUCTURE_COLORS[normalizedStructure as keyof typeof STRUCTURE_COLORS] || 'bg-gray-50 text-gray-700 border-gray-200';
  const label = STRUCTURE_LABELS[normalizedStructure] || structure;
  const description = STRUCTURE_DESCRIPTIONS[normalizedStructure];

  // If we have a description, show with tooltip
  if (description) {
    return (
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={`text-xs h-5 cursor-help flex items-center gap-1 ${colorClass} ${className}`}
            onClick={() => setOpen(!open)}
          >
            {Icon && <Icon size={12} />}
            {label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">{description.title}</p>
            <p className="text-xs opacity-90">{description.description}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  // If no description, show simple badge
  return (
    <Badge
      variant="outline"
      className={`text-xs h-5 flex items-center gap-1 ${colorClass} ${className}`}
    >
      {Icon && <Icon size={12} />}
      {label}
    </Badge>
  );
}

